<script setup lang="ts">
import { UiText, UiButton, UiIcon } from "@shopexperts/shared";
import { useExpertLeads } from "@/app/composables/leads/useExpertLeads";
import ExpertLeadCard from "@/app/components/leads/ExpertLeadCard.vue";
import PageHeader from "@/app/components/common/PageHeader.vue";

// Fetch leads with pagination
const { 
  leads, 
  isLoading, 
  error, 
  refetch,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
} = useExpertLeads({ limit: 10 });
</script>

<template>
  <main class="flex-1 p-4 md:p-8 overflow-y-auto bg-secondary font-light w-full">
    <PageHeader
      title="Your Leads"
      description="Manage all your matched leads in one place.<br />Connect with clients and grow your business."
    />

    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-12">
      <div class="flex flex-col items-center gap-3">
        <div class="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        <UiText variant="p" as="p" class="text-gray-500">Loading your leads...</UiText>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-12">
      <div class="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
        <UiIcon name="close" class="w-12 h-12 text-red-500 mx-auto mb-3" />
        <UiText variant="p" as="p" class="text-red-800 font-semibold mb-2">
          Failed to load leads
        </UiText>
        <UiText variant="p-small" as="p" class="text-red-600 mb-4">
          {{ (error as Error).message || 'An error occurred while fetching your leads' }}
        </UiText>
        <UiButton variant="outline" label="Try Again" @click="refetch" />
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="leads.length === 0" class="text-center py-12">
      <div class="flex flex-col items-center gap-4 max-w-md mx-auto">
        <div class="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center">
          <UiIcon name="package-placeholder" class="w-10 h-10 text-gray-400" />
        </div>
        <div>
          <UiText variant="h6" as="h6" class="font-semibold mb-2">
            No leads yet
          </UiText>
          <UiText variant="p-small" as="p" class="text-gray-600">
            You don't have any matched leads yet. Complete your profile to attract more clients and get matched with projects.
          </UiText>
        </div>
      </div>
    </div>

    <!-- Leads List -->
    <div v-else class="flex flex-col gap-4">
      <ExpertLeadCard
        v-for="lead in leads"
        :key="lead.id"
        :lead="lead"
      />

      <!-- Show More Button -->
      <div v-if="hasNextPage" class="flex justify-center pt-4">
        <UiButton 
          variant="outline" 
          :label="isFetchingNextPage ? 'Loading...' : 'Show More'"
          :loading="isFetchingNextPage"
          @click="fetchNextPage"
        />
      </div>

      <!-- Loading indicator when fetching next page -->
      <div v-if="isFetchingNextPage" class="flex justify-center py-4">
        <div class="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    </div>
  </main>
</template>
